import type { BreakdownRow, ChangeMagnitude, FilterOption, SummaryMode } from './types';

const LOCALE = 'is-IS';

export const SUPPRESSED_LABEL = 'Ekki nóg gögn (<10)';
export const EMPTY_VALUE = '–';
export const UNKNOWN_LABEL = 'Óþekkt';

export const MAGNITUDE_LABELS: Record<ChangeMagnitude, string> = {
  minimal: 'Lítilsháttar',
  moderate: 'Í meðallagi',
  major: 'Miklar',
};

// Reused by the mode filter dropdown and the breakdown table, so both show the
// same Icelandic wording for a mode value.
export const MODE_LABELS: Record<SummaryMode, string> = {
  conversation: 'Samtal',
  dictation: 'Upplestur',
};

export const MODE_OPTIONS: FilterOption[] = [
  { value: 'conversation', label: MODE_LABELS.conversation },
  { value: 'dictation', label: MODE_LABELS.dictation },
];

/** A breakdown row's raw `mode` value ('conversation'/'dictation') to its Icelandic label. */
export const formatModeLabel = (value: string | null): string => {
  if (value === 'conversation' || value === 'dictation') return MODE_LABELS[value];
  return value || UNKNOWN_LABEL;
};

export const formatNumber = (value: number) => new Intl.NumberFormat(LOCALE).format(Math.round(value || 0));

export const formatPercent = (rate: number | null, digits = 1) =>
  rate == null
    ? EMPTY_VALUE
    : new Intl.NumberFormat(LOCALE, {
        style: 'percent',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(rate);

export const formatLatency = (ms: number | null) => {
  if (ms == null) return EMPTY_VALUE;
  if (ms < 1000) return `${new Intl.NumberFormat(LOCALE).format(Math.round(ms))} ms`;
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 }).format(ms / 1000)} s`;
};

export const formatSeconds = (seconds: number | null) =>
  seconds == null ? EMPTY_VALUE : `${new Intl.NumberFormat(LOCALE).format(Math.round(seconds))} s`;

export const formatRatio = (value: number | null) =>
  value == null ? EMPTY_VALUE : new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 }).format(value);

export const formatDay = (isoDate: string | null) =>
  isoDate
    ? new Date(isoDate).toLocaleDateString(LOCALE, { year: 'numeric', month: 'short', day: 'numeric' })
    : EMPTY_VALUE;

/**
 * Breakdown rows as dropdown options. Rows without a key can't be filtered on, and
 * repeated labels (two people with one name, every unresolved org) get a counter so
 * the options stay distinguishable without ever showing the underlying id.
 */
export const toFilterOptions = (rows: BreakdownRow[]): FilterOption[] => {
  const seen = new Map<string, number>();
  return rows
    .filter((row): row is BreakdownRow & { key: string } => row.key != null)
    .map((row) => {
      const label = row.label || UNKNOWN_LABEL;
      const count = (seen.get(label) ?? 0) + 1;
      seen.set(label, count);
      return { value: row.key, label: count > 1 ? `${label} (${count})` : label };
    });
};
